import { useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Delete } from '@mui/icons-material';
import type { APIKeyMetadata } from 'api-client';

export interface APIKeyManagementProps {
  keys: APIKeyMetadata[];
  pending: boolean;
  error: string | null;
  success: string | null;
  newKey: string | null;
  onCreate: () => void;
  onRevoke: (key: APIKeyMetadata) => Promise<boolean>;
  onDismissKey: () => void;
}
export default function APIKeyManagement(props: APIKeyManagementProps) {
  const [selected, setSelected] = useState<APIKeyMetadata | null>(null);
  const [attempted, setAttempted] = useState<boolean>(false);
  const [copyResult, setCopyResult] = useState<string | null>(null);
  const copy = async () => {
    if (!props.newKey) return;
    try {
      await navigator.clipboard.writeText(props.newKey);
      setCopyResult('API key copied.');
    } catch {
      setCopyResult('Could not copy automatically. Select and copy the key.');
    }
  };
  return (
    <>
      <Stack spacing={2}>
        {props.error && !selected && <Alert severity="error">{props.error}</Alert>}
        {props.success && <Alert severity="success">{props.success}</Alert>}
        {props.keys.length === 0 ? (
          <Typography color="text.secondary">You have no active API keys.</Typography>
        ) : (
          <TableContainer role="region" aria-label="Active API keys" tabIndex={0}>
            <Table size="small" aria-label="API key metadata">
              <TableHead>
                <TableRow>
                  <TableCell>Prefix</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {props.keys.map((key) => (
                  <TableRow key={key.id}>
                    <TableCell>{key.prefix}</TableCell>
                    <TableCell>{key.created.toLocaleDateString()}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        aria-label={`Revoke API key ${key.prefix}`}
                        color="error"
                        disabled={props.pending}
                        onClick={() => {
                          setSelected(key);
                          setAttempted(false);
                        }}
                      >
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        <Button
          variant="outlined"
          onClick={props.onCreate}
          disabled={props.pending}
          loading={props.pending && !selected}
          loadingPosition="start"
          sx={{ alignSelf: 'flex-start' }}
        >
          {props.pending && !selected ? 'Creating key…' : 'New API Key'}
        </Button>
        <Typography variant="body2" color="text.secondary">
          Full keys appear once at creation. Revoking a key immediately stops its access.
        </Typography>
      </Stack>
      <Dialog
        open={!!selected}
        onClose={() => {
          if (!props.pending) setSelected(null);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Revoke API Key</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <DialogContentText>
              Revoke {selected?.prefix}? Applications using this key will lose access.
            </DialogContentText>
            {attempted && props.error && <Alert severity="error">{props.error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button disabled={props.pending} onClick={() => setSelected(null)}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={props.pending}
            loading={props.pending}
            loadingPosition="start"
            onClick={() => {
              if (!selected) return;
              setAttempted(true);
              void props.onRevoke(selected).then((done) => {
                if (done) setSelected(null);
              });
            }}
          >
            {props.pending ? 'Revoking key…' : 'Revoke Key'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={!!props.newKey}
        onClose={() => {
          props.onDismissKey();
          setCopyResult(null);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>New API Key</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Alert severity="warning">Copy and store this key securely now. It will not be shown again.</Alert>
            <TextField
              label="Full API key"
              value={props.newKey || ''}
              multiline
              fullWidth
              slotProps={{ input: { readOnly: true } }}
            />
            {copyResult && <Typography role="status">{copyResult}</Typography>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              void copy();
            }}
          >
            Copy Key
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              props.onDismissKey();
              setCopyResult(null);
            }}
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
